import { createTheme } from '@mui/material/styles'
import { ThemedColors, ThemeOptions } from './typings'
import createShadow from './util/createShadow'
import baseThemeFor from './base'
import components from './overrides/components'
import breakpoints from './overrides/breakpoints'
import { CSSProperties } from 'react'
import typography from './overrides/typography'

declare module '@mui/material/styles' {
  interface Theme {
    ues: ThemedColors
  }
  interface TypographyVariants {
    body3: CSSProperties
  }

  // allow configuration using `createTheme`
  interface ThemeOptions {
    ues: ThemedColors
  }
  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    body3?: CSSProperties
  }
}
// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    body3: true
    subtitle1: false
    subtitle2: false
  }
}

const createUESTheme = (options?: ThemeOptions) => {
  const componentOverrides = options?.componentOverrides || {}
  const baseTheme = baseThemeFor()
  return createTheme({
    palette: {
      mode: 'light',
      common: {
        white: baseTheme.neutral['0'],
        black: baseTheme.neutral['120'],
      },
      primary: {
        main: baseTheme.primary['100'],
      },
      secondary: {
        main: baseTheme.primary['70'],
      },
      info: {
        light: baseTheme.info['5'],
        dark: baseTheme.info['70'],
        main: baseTheme.info['70'],
        contrastText: baseTheme.neutral['110'],
      },
      error: {
        light: baseTheme.error['5'],
        dark: baseTheme.error['70'],
        main: baseTheme.error['70'],
        contrastText: baseTheme.neutral['110'],
      },
      warning: {
        light: baseTheme.warning['5'],
        dark: baseTheme.warning['70'],
        main: baseTheme.warning['70'],
        contrastText: baseTheme.neutral['110'],
      },
      success: {
        light: baseTheme.success['5'],
        dark: baseTheme.success['70'],
        main: baseTheme.success['70'],
        contrastText: baseTheme.neutral['110'],
      },
      text: {
        primary: baseTheme.neutral['90'],
        secondary: baseTheme.neutral['80'],
        disabled: baseTheme.neutral['70'],
      },
      background: {
        default: baseTheme.neutral['20'],
        paper: baseTheme.neutral['0'],
      },
    },
    ues: baseTheme,
    shadows: [
      'none',
      'none',
      createShadow(0, 0, 2, 0, baseTheme.neutral['110'], 16),
      createShadow(0, 0, 3, 0, baseTheme.neutral['110'], 16),
      createShadow(0, 1, 4, 0, baseTheme.neutral['110'], 16),
      createShadow(0, 1, 5, 0, baseTheme.neutral['110'], 16),
      createShadow(0, 2, 6, 0, baseTheme.neutral['110'], 16),
      createShadow(0, 2, 7, 0, baseTheme.neutral['110'], 16),
      createShadow(0, 4, 8, 0, baseTheme.neutral['110'], 16),
      createShadow(0, 4, 9, 0, baseTheme.neutral['110'], 16),
      createShadow(0, 4, 10, 0, baseTheme.neutral['110'], 16),
      createShadow(0, 4, 11, 0, baseTheme.neutral['110'], 16),
      createShadow(0, 4, 12, 0, baseTheme.neutral['110'], 16),
      createShadow(0, 4, 13, 0, baseTheme.neutral['110'], 16),
      createShadow(0, 4, 14, 0, baseTheme.neutral['110'], 16),
      createShadow(0, 4, 15, 0, baseTheme.neutral['110'], 16),
      createShadow(0, 4, 16, 0, baseTheme.neutral['110'], 16),
      createShadow(0, 4, 17, 0, baseTheme.neutral['110'], 16),
      createShadow(0, 4, 18, 0, baseTheme.neutral['110'], 16),
      createShadow(0, 4, 19, 0, baseTheme.neutral['110'], 16),
      createShadow(0, 6, 20, 0, baseTheme.neutral['110'], 16),
      createShadow(0, 6, 21, 0, baseTheme.neutral['110'], 16),
      createShadow(0, 6, 22, 0, baseTheme.neutral['110'], 16),
      createShadow(0, 6, 23, 0, baseTheme.neutral['110'], 16),
      createShadow(0, 8, 24, 0, baseTheme.neutral['110'], 16),
    ],
    typography: {
      fontFamily: [baseTheme.fontFamily, 'sans-serif'].join(','),
      h1: {
        ...typography.h1,
        fontWeight: 700,
        color: baseTheme.primary['100'],
        [`@media (max-width:${breakpoints.values?.md || 700}px)`]: {
          ...typography.h2,
        },
        [`@media (max-width:${breakpoints.values?.sm || 500}px)`]: {
          ...typography.h3,
        },
      },
      h2: {
        ...typography.h2,
        fontWeight: 700,
        color: baseTheme.primary['100'],
        [`@media (max-width:${breakpoints.values?.md || 700}px)`]: {
          ...typography.h3,
        },
        [`@media (max-width:${breakpoints.values?.sm || 500}px)`]: {
          ...typography.h4,
        },
      },
      h3: {
        ...typography.h3,
        fontWeight: 700,
        color: baseTheme.primary['100'],
        [`@media (max-width:${breakpoints.values?.md || 700}px)`]: {
          ...typography.h4,
        },
        [`@media (max-width:${breakpoints.values?.sm || 500}px)`]: {
          ...typography.h5,
        },
      },
      h4: {
        ...typography.h4,
        fontWeight: 700,
        color: baseTheme.neutral['90'],
        [`@media (max-width:${breakpoints.values?.md || 700}px)`]: {
          ...typography.h5,
        },
        [`@media (max-width:${breakpoints.values?.sm || 500}px)`]: {
          ...typography.h6,
        },
      },
      h5: {
        ...typography.h5,
        fontWeight: 700,
        color: baseTheme.neutral['90'],
        [`@media (max-width:${breakpoints.values?.md || 700}px)`]: {
          ...typography.h6,
        },
        [`@media (max-width:${breakpoints.values?.sm || 500}px)`]: {
          ...typography.body1,
        },
      },
      h6: {
        ...typography.h6,
        fontWeight: 700,
        color: baseTheme.neutral['90'],
        [`@media (max-width:${breakpoints.values?.md || 700}px)`]: {
          ...typography.body1,
        },
        [`@media (max-width:${breakpoints.values?.sm || 500}px)`]: {
          ...typography.body2,
        },
      },
      body1: {
        ...typography.body1,
        color: baseTheme.neutral['80'],
        [`@media (max-width:${breakpoints.values?.md || 700}px)`]: {
          ...typography.body2,
        },
      },
      body2: {
        ...typography.body2,
        color: baseTheme.neutral['80'],
        [`@media (max-width:${breakpoints.values?.md || 700}px)`]: {
          ...typography.body3,
        },
      },
      body3: {
        ...typography.body3,
        color: baseTheme.neutral['80'],
        fontFamily: [baseTheme.fontFamily, 'sans-serif'].join(','),
        fontWeight: 400,
      },
      subtitle1: undefined,
      subtitle2: undefined,
      caption: {
        ...typography.caption,
        color: baseTheme.neutral['80'],
      },
    },
    breakpoints: {
      ...breakpoints,
    },
    components: {
      MuiCssBaseline: {},
      MuiUseMediaQuery: {
        defaultProps: {
          noSsr: true,
        },
      },
      ...components(options?.enableToolkitFocus),
      ...componentOverrides,
    },
  })
}

export default createUESTheme
